import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { publicApiGet } from '../lib/api';
import { useStore } from '../store/useStore';
import { Button } from '../components/ui/button';
import { DRIVE_NAME, FILE_COLORS } from '../components/office/icons';
import { BrandLogo } from '../components/BrandLogo';
import '../components/drive/drive.css';
import {
  Folder, FileText, Table2, Presentation, FileType2, File, FileCode2, FileArchive,
  Image as ImageIcon, Clapperboard, Music2, Workflow, ClipboardList, ChevronRight, Loader2, Lock, Users, Download,
} from 'lucide-react';
import { descargarArchivo } from '../components/office/descargas';
import { fechaHora12 } from '../lib/format';

/** Mismos colores e iconos que Drive (FILE_COLORS es el único sitio donde viven). */
const ICONO = {
  doc: { Icon: FileText, color: FILE_COLORS.doc },
  sheet: { Icon: Table2, color: FILE_COLORS.sheet },
  slides: { Icon: Presentation, color: FILE_COLORS.slides },
  pdf: { Icon: FileType2, color: FILE_COLORS.pdf },
  diagram: { Icon: Workflow, color: FILE_COLORS.diagram },
  form: { Icon: ClipboardList, color: FILE_COLORS.form },
  image: { Icon: ImageIcon, color: FILE_COLORS.image },
  text: { Icon: FileCode2, color: FILE_COLORS.neutral },
  archive: { Icon: FileArchive, color: FILE_COLORS.neutral },
  video: { Icon: Clapperboard, color: FILE_COLORS.pdf },
  audio: { Icon: Music2, color: FILE_COLORS.slides },
  file: { Icon: File, color: FILE_COLORS.neutral },
};

const PAPEL = {
  owner: 'Propietario',
  editor: 'Puede editar',
  commenter: 'Puede comentar',
  viewer: 'Solo puede ver',
};

function fmtSize(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return fechaHora12(iso);
  } catch {
    return '';
  }
}

/**
 * Una carpeta compartida por su enlace general: `/carpeta/<token>`.
 *
 * Sin cuenta si el acceso es «Cualquier persona con el enlace»; con cuenta si es de la organización
 * o de un dominio. Cada archivo se abre con su propio enlace (`<token>.<id>`, lo arma el backend),
 * que va al dominio que le toca — docs.orvian.mx para los documentos. Como la pestaña de un
 * documento compartido, esta pregunta cada minuto si el enlace sigue vivo: al restringirlo, la
 * vista se cambia sola sin recargar.
 */
export default function CarpetaCompartida() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const user = useStore((s) => s.user);
  const ruta = params.get('ruta') || '/';
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setCargando(true);
    try {
      const { data } = await publicApiGet(`/drive/carpeta/${token}?ruta=${encodeURIComponent(ruta)}`);
      setDatos(data);
      setError(null);
      document.title = `${data?.carpeta?.name || 'Carpeta'} · ${DRIVE_NAME}`;
    } catch (err) {
      const st = err.response?.status;
      setError({
        status: st,
        detalle: typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'No se pudo abrir la carpeta',
      });
    } finally {
      setCargando(false);
    }
  }, [token, ruta]);

  useEffect(() => { cargar(); }, [cargar]);

  // El enlace se vigila mientras la pestaña esté abierta (igual que en un documento compartido)
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) cargar({ silencioso: true }); }, 60000);
    return () => clearInterval(t);
  }, [cargar]);

  const entrar = () => {
    try {
      sessionStorage.setItem('zuno_next', `/carpeta/${token}${ruta !== '/' ? `?ruta=${encodeURIComponent(ruta)}` : ''}`);
    } catch {
      /* sin sessionStorage se vuelve al correo y ya */
    }
    nav('/login');
  };

  const migas = useMemo(() => datos?.migas || [], [datos]);

  if (cargando && !datos) {
    return (
      <div className="gdrive-root h-screen grid place-items-center" style={{ background: 'var(--gd-bg)' }}>
        <Loader2 className="h-10 w-10 animate-spin" style={{ color: 'var(--gd-blue)' }} />
      </div>
    );
  }

  if (error) {
    const pideSesion = error.status === 401;
    return (
      <div className="gdrive-root h-screen grid place-items-center px-6" style={{ background: 'var(--gd-bg)', color: 'var(--gd-text)' }}>
        <div className="text-center max-w-md">
          <div className="gdrive-empty-icon mx-auto"><Lock className="h-9 w-9" strokeWidth={1.75} /></div>
          <p className="text-lg font-semibold mt-3">
            {pideSesion ? 'Esta carpeta es de la organización' : 'El enlace ya no está disponible'}
          </p>
          <p className="text-sm mt-1" style={{ color: 'var(--gd-text-sec)' }}>{error.detalle}</p>
          {pideSesion && (
            <Button className="rounded-xl mt-4" onClick={entrar} data-testid="carpeta-entrar">Iniciar sesión</Button>
          )}
        </div>
      </div>
    );
  }

  const { carpeta, items = [], carpetas = [], my_role: papel } = datos || {};

  return (
    <div className="gdrive-root h-screen overflow-y-auto flex flex-col" style={{ background: 'var(--gd-bg)', color: 'var(--gd-text)' }} data-testid="carpeta-compartida">
      <header className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'var(--gd-border)' }}>
        <BrandLogo showText={false} className="shrink-0" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <Folder className="h-4 w-4 shrink-0" style={{ color: 'var(--gd-text-sec)' }} />
            <span className="font-medium truncate">{carpeta?.name}</span>
          </div>
          <div className="text-xs truncate" style={{ color: 'var(--gd-text-sec)' }}>
            {carpeta?.owner_name ? `Compartida por ${carpeta.owner_name}` : 'Carpeta compartida'}
            {papel ? ` · ${PAPEL[papel] || papel}` : ''}
          </div>
        </div>
        <div className="ml-auto shrink-0">
          {user ? (
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => nav('/drive')}>
              <Users className="h-4 w-4 mr-1" /> Mi {DRIVE_NAME}
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="rounded-full" onClick={entrar}>Iniciar sesión</Button>
          )}
        </div>
      </header>

      <div className="gdrive-crumbs px-4 pt-3">
        {migas.map((m, i) => (
          <React.Fragment key={m.ruta}>
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 opacity-50" />}
            <button
              type="button"
              className={i === migas.length - 1 ? 'current' : ''}
              onClick={() => setParams(m.ruta === '/' ? {} : { ruta: m.ruta })}
            >
              {m.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="flex-1 px-2 md:px-4 pb-8">
        {!items.length && !carpetas.length ? (
          <div className="gdrive-empty">
            <div className="gdrive-empty-icon"><Folder className="h-9 w-9" strokeWidth={1.75} /></div>
            <p className="title">Esta carpeta está vacía</p>
            <p className="sub">Cuando su propietario añada archivos, aparecerán aquí.</p>
          </div>
        ) : (
          <table className="gdrive-table w-full">
            <thead>
              <tr>
                <th className="text-left">Nombre</th>
                <th className="text-left hidden sm:table-cell">Última modificación</th>
                <th className="text-left hidden lg:table-cell">Tamaño</th>
                <th aria-label="Descargar" />
              </tr>
            </thead>
            <tbody>
              {carpetas.map((c) => (
                <tr
                  key={c.ruta}
                  className="gdrive-row is-carpeta"
                  onClick={() => setParams({ ruta: c.ruta })}
                  // Con teclado también (D13): Tab llega a la fila y Enter la abre
                  tabIndex={0}
                  role="link"
                  onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setParams({ ruta: c.ruta }); } }}
                  data-testid="carpeta-subcarpeta"
                >
                  <td>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="gdrive-file-icon" style={{ background: 'var(--gd-hover)' }}>
                        <Folder className="h-4 w-4" style={{ color: 'var(--gd-text-sec)' }} />
                      </span>
                      <span className="name truncate">{c.name}</span>
                    </div>
                  </td>
                  <td className="hidden sm:table-cell sec">{fmtDate(c.updated_at) || '—'}</td>
                  <td className="hidden lg:table-cell sec">{fmtSize(c.size)}</td>
                  <td />
                </tr>
              ))}
              {items.map((f) => {
                const meta = ICONO[f.kind] || ICONO.file;
                const Icon = meta.Icon;
                return (
                  <tr
                    key={f.id}
                    className="gdrive-row"
                    onClick={() => { window.location.assign(f.url_publica); }}
                    tabIndex={0}
                    role="link"
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return;   // Enter en «Descargar» es de ese botón
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); window.location.assign(f.url_publica); }
                    }}
                    data-testid="carpeta-archivo"
                  >
                    <td>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="gdrive-file-icon" style={{ background: `${meta.color}1f` }}>
                          <Icon className="h-4 w-4" style={{ color: meta.color }} />
                        </span>
                        <span className="name truncate">{f.name}</span>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell sec">{fmtDate(f.updated_at)}</td>
                    <td className="hidden lg:table-cell sec">{fmtSize(f.size)}</td>
                    <td className="w-12 text-right">
                      {f.kind !== 'form' && (
                        <button
                          type="button"
                          className="gdrive-icon-btn"
                          title="Descargar"
                          aria-label={`Descargar ${f.name}`}
                          data-testid="carpeta-descargar"
                          onClick={(e) => {
                            e.stopPropagation();
                            // El enlace de un archivo dentro de la carpeta es `<token>.<id>` (el backend lo arma)
                            const enlace = (f.url_publica || '').split('/open/')[1] || `${token}.${f.id}`;
                            descargarArchivo({ fileId: f.id, link: enlace, nombre: f.name });
                          }}
                        >
                          <Download className="h-[18px] w-[18px]" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
